const PRODUCT_SLUGS = ['oneeduca'] as const;

export const PRODUCT_NOT_ACCESSIBLE_MESSAGE =
  'OneEduca não está disponível para sua empresa no momento. Contrate ou regularize o pagamento no Admin Center.';

export const USER_INACTIVE_MESSAGE =
  'Seu acesso está temporariamente inativo. Procure o responsável ou o administrador da instituição para reativar seu cadastro.';

interface JwtProductClaim {
  slug?: string;
  accessible?: boolean;
  release_state?: string | null;
  trial_days_remaining?: number | null;
  show_trial_banner?: boolean;
}

export interface OneEducaTrialBanner {
  readonly daysRemaining: number;
  readonly message: string;
}

export function decodeJwtPayload(
  token: string,
): Record<string, unknown> | null {
  const parts = token.split('.');

  if (parts.length !== 3) {
    return null;
  }

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    );
    const json = atob(padded);

    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function findOneEducaProduct(token: string): JwtProductClaim | null {
  const payload = decodeJwtPayload(token);

  if (payload === null) {
    return null;
  }

  const products = payload['products'];

  if (!Array.isArray(products) || products.length === 0) {
    return null;
  }

  for (const item of products) {
    if (typeof item !== 'object' || item === null) {
      continue;
    }

    const product = item as JwtProductClaim;
    const slug = product.slug ?? '';

    if (!PRODUCT_SLUGS.includes(slug as (typeof PRODUCT_SLUGS)[number])) {
      continue;
    }

    return product;
  }

  return null;
}

export function isProductAccessibleInJwt(token: string): boolean {
  const product = findOneEducaProduct(token);

  if (product === null) {
    const payload = decodeJwtPayload(token);

    if (payload === null) {
      return false;
    }

    const products = payload['products'];

    // Sem claim de produtos: não bloqueia (compatibilidade).
    if (!Array.isArray(products) || products.length === 0) {
      return true;
    }

    return false;
  }

  return product.accessible === true;
}

export function getOneEducaTrialBannerFromJwt(
  token: string,
): OneEducaTrialBanner | null {
  const product = findOneEducaProduct(token);

  if (product === null || product.accessible !== true) {
    return null;
  }

  if (
    product.release_state !== 'trialing' ||
    product.show_trial_banner !== true
  ) {
    return null;
  }

  if (
    product.trial_days_remaining === null ||
    product.trial_days_remaining === undefined ||
    !Number.isFinite(product.trial_days_remaining)
  ) {
    return null;
  }

  const daysRemaining = Math.max(0, Math.floor(product.trial_days_remaining));
  const daysLabel =
    daysRemaining === 1 ? 'Resta 1 dia' : `Restam ${daysRemaining} dias`;

  return {
    daysRemaining,
    message: `Você está em período de teste do OneEduca. ${daysLabel}.`,
  };
}
